import {
  PlusCircle,
  MoreHorizontal,
  Edit2,
  Trash,
  Search,
  X,
  Check,
  GalleryVerticalEnd,
} from 'lucide-react';
import {
  Sidebar,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '~/components/ui/Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Input } from '../ui/Input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { ScrollArea } from '../ui/ScrollArea';
import { useChats, useUpdateChat, useDeleteChat } from '~/hooks/use-ai-chat';
import { cn } from '~/lib/utils';

const LOADING_SKELETONS = [1, 2, 3, 4, 5];
const LOAD_MORE_SKELETONS = [1, 2, 3];

export default function ChatSidebarContent({
  className,
  isCollapsed,
  setIsCollapsed,
}: {
  className?: string;
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
}) {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const {
    data: chats = [],
    isLoading,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useChats();
  const updateChatMutation = useUpdateChat();
  const deleteChatMutation = useDeleteChat();
  const { setOpenMobile, isMobile } = useSidebar();
  const [renamingChatId, setRenamingChatId] = useState<number | null>(null);
  const [inlineRenameValue, setInlineRenameValue] = useState('');
  const inlineInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{
    id: number;
    title: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingChatId, setPendingChatId] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const activeItemRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return chats;
    return chats.filter((chat) =>
      chat.title.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [chats, searchQuery]);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore || searchQuery.trim()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, isLoadingMore, searchQuery]);

  useEffect(() => {
    const item = activeItemRef.current;
    const viewport = viewportRef.current;
    if (!item || !viewport) return;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const viewTop = viewport.scrollTop;
    const viewBottom = viewTop + viewport.clientHeight;
    if (itemTop < viewTop) {
      viewport.scrollTop = itemTop - 8;
    } else if (itemBottom > viewBottom) {
      viewport.scrollTop = itemBottom - viewport.clientHeight + 8;
    }
  }, [chatId]);

  useEffect(() => {
    setSearchQuery('');
  }, [chatId]);

  useEffect(() => {
    if (pendingChatId && pendingChatId === chatId) {
      setPendingChatId(null);
    }
  }, [chatId, pendingChatId]);

  useEffect(() => {
    if (renamingChatId !== null) {
      setTimeout(() => inlineInputRef.current?.focus(), 0);
    }
  }, [renamingChatId]);

  const handleOpenChat = (id: number) => {
    setPendingChatId(String(id));
    navigate(`/chat/${id}`);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleCreateNewChat = () => {
    navigate('/chat');
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleStartRename = (id: number, currentTitle: string) => {
    setRenamingChatId(id);
    setInlineRenameValue(currentTitle);
  };

  const handleRenameConfirm = useCallback(async () => {
    if (!renamingChatId || !inlineRenameValue.trim()) return;
    try {
      await updateChatMutation.mutateAsync({
        id: renamingChatId,
        title: inlineRenameValue.trim(),
      });
    } finally {
      setRenamingChatId(null);
      setInlineRenameValue('');
    }
  }, [renamingChatId, inlineRenameValue, updateChatMutation]);

  const handleRenameCancel = () => {
    setRenamingChatId(null);
    setInlineRenameValue('');
  };

  const handleDeleteConfirm = (chatId: number, chatTitle: string) => {
    setChatToDelete({ id: chatId, title: chatTitle });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!chatToDelete) return;
    try {
      await deleteChatMutation.mutateAsync(chatToDelete.id);
      setDeleteDialogOpen(false);
      setChatToDelete(null);
      if (String(chatToDelete.id) === chatId) {
        navigate('/chat');
      }
    } catch {}
  };

  return (
    <Sidebar
      collapsible="none"
      className={cn(
        'h-full w-full max-w-full flex flex-col bg-sidebar rounded-tr-xl overflow-hidden transition-all duration-300',
        className,
      )}
    >
      <div
        className={cn(
          'flex-none border-b transition-all duration-300',
          isCollapsed ? 'p-3 space-y-3' : 'p-4 space-y-3',
        )}
      >
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleCreateNewChat}
              className={cn(
                'bg-primary! text-primary-foreground! hover:bg-primary/90! hover:text-primary-foreground! transition-all duration-200',
                isCollapsed ? 'w-full p-0 justify-center mx-auto' : 'w-full',
              )}
              title={isCollapsed ? 'New chat' : undefined}
            >
              <PlusCircle
                className={cn('shrink-0', isCollapsed ? 'size-5' : 'size-4')}
              />
              {!isCollapsed && <span>New chat</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <div className="relative">
          {isCollapsed ? (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="icon"
                className="size-10 rounded-md hover:bg-accent text-muted-foreground"
                title="Search chats"
                onClick={() => {
                  setIsCollapsed?.(false);
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 150);
                }}
              >
                <Search className="size-5" />
              </Button>
            </div>
          ) : (
            <>
              <Search className="absolute left-3 inset-y-0 my-auto size-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 inset-y-0 my-auto h-fit hover:bg-accent rounded p-0.5"
                >
                  <X className="size-3 text-muted-foreground" />
                </button>
              )}
            </>
          )}
        </div>

        {!isCollapsed && searchQuery && (
          <div className="text-xs font-medium text-muted-foreground px-1">
            Search Results ({filteredChats.length})
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {isCollapsed ? (
          <div className="flex justify-center py-2">
            <Button
              variant="ghost"
              size="icon"
              className="size-10 rounded-md hover:bg-accent text-muted-foreground"
              title="View chats"
              onClick={() => setIsCollapsed?.(false)}
            >
              <GalleryVerticalEnd className="size-5" />
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-full w-full" viewportRef={viewportRef}>
            <div className="p-2 space-y-1">
              {isLoading ? (
                <>
                  {LOADING_SKELETONS.map((i) => (
                    <SidebarMenuItem key={i}>
                      <div className="flex items-center gap-2 py-1.5">
                        <Skeleton className="size-4 shrink-0" />
                        <Skeleton className="h-4 flex-1" />
                      </div>
                    </SidebarMenuItem>
                  ))}
                </>
              ) : filteredChats.length > 0 ? (
                filteredChats.map((c) => {
                  const isActive = String(c.id) === chatId;
                  const isRenaming = renamingChatId === c.id;
                  const containerClasses = cn(
                    'flex items-center rounded-lg group w-full min-w-0 transition-all duration-200',
                    isMobile && 'justify-between',
                    isActive
                      ? 'bg-linear-to-r from-gray-200 to-gray-100 dark:from-gray-800 dark:to-gray-900/80 border border-gray-300 dark:border-gray-700 shadow-sm'
                      : 'hover:bg-linear-to-r hover:from-gray-100/80 hover:to-gray-50 dark:hover:from-gray-800/50 dark:hover:to-gray-900/30 border border-transparent hover:border-gray-200 dark:hover:border-gray-800',
                  );
                  const buttonClasses = cn(
                    'flex-1 min-w-0 text-left p-2 pr-1 text-xs sm:text-sm hover:bg-transparent transition-all duration-200',
                    isActive
                      ? 'text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-foreground',
                  );
                  const menuButtonClasses = cn(
                    'p-1.5 mr-1 rounded-md transition-all data-[state=open]:opacity-100 shrink-0',
                    isActive
                      ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
                      : 'text-muted-foreground hover:bg-gray-200 dark:hover:bg-gray-700',
                    isMobile
                      ? 'opacity-100'
                      : 'opacity-0 group-hover:opacity-100',
                  );

                  return (
                    <div
                      className="w-full min-w-0"
                      key={c.id}
                      ref={isActive ? activeItemRef : undefined}
                    >
                      <div className={containerClasses}>
                        {isRenaming ? (
                          <div className="flex-1 flex items-center gap-1 px-1 py-1 min-w-0">
                            <Input
                              ref={inlineInputRef}
                              value={inlineRenameValue}
                              onChange={(e) =>
                                setInlineRenameValue(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameConfirm();
                                if (e.key === 'Escape') handleRenameCancel();
                              }}
                              onBlur={handleRenameCancel}
                              className="h-7 text-xs px-2 flex-1 min-w-0"
                            />
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleRenameConfirm();
                              }}
                              disabled={
                                updateChatMutation.isPending ||
                                !inlineRenameValue.trim()
                              }
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-40 shrink-0"
                            >
                              <Check className="size-3.5 text-green-600 dark:text-green-400" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleOpenChat(c.id)}
                            className={cn(buttonClasses, 'group/chat-item')}
                            style={{ width: 0 }}
                            title={c.title}
                          >
                            <span
                              className="block truncate max-w-full group-hover/chat-item:max-w-[95%] transition-all duration-150"
                              title={c.title}
                            >
                              {c.title}
                            </span>
                          </button>
                        )}
                        {!isRenaming && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className={menuButtonClasses}>
                                <MoreHorizontal className="size-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="text-xs sm:text-sm"
                            >
                              <DropdownMenuItem
                                onClick={() => handleStartRename(c.id, c.title)}
                                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                              >
                                <Edit2 className="size-3 mr-1.5" />
                                Rename
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  handleDeleteConfirm(c.id, c.title)
                                }
                                className="cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/30"
                              >
                                <Trash className="size-3 mr-1.5 text-red-600 dark:text-red-400" />
                                <span className="text-red-600 dark:text-red-400">
                                  Delete
                                </span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-6 text-center text-xs sm:text-sm text-muted-foreground">
                  {searchQuery ? 'No chats found' : 'No recent chats'}
                </div>
              )}
            </div>

            {!searchQuery.trim() && (
              <div ref={loadMoreRef} className="pb-2 space-y-2">
                {isLoadingMore && (
                  <>
                    {LOAD_MORE_SKELETONS.map((i) => (
                      <SidebarMenuItem key={`load-more-skeleton-${i}`}>
                        <Skeleton className="h-6 mx-2 sm:mx-4" />
                      </SidebarMenuItem>
                    ))}
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        )}
      </div>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (deleteChatMutation.isPending) return;
          setDeleteDialogOpen(open);
          if (!open) setChatToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-lg max-w-[92vw] gap-6">
          <DialogHeader className="space-y-3">
            <DialogTitle className="text-xl font-semibold text-red-600 dark:text-red-400">
              Delete Chat
            </DialogTitle>
            <DialogDescription className="break-all overflow-hidden text-sm leading-relaxed">
              Are you sure you want to permanently delete{' '}
              <span className="font-semibold text-foreground break-all inline-block max-w-full px-1.5 py-0.5 bg-muted rounded">
                {chatToDelete?.title}
              </span>
              ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteChatMutation.isPending}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteChatMutation.isPending}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white w-full sm:w-auto"
            >
              {deleteChatMutation.isPending
                ? 'Deleting…'
                : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
